import { useEffect, useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { TextField, RadioField, SwitchField } from "@/components/form";
import { onboarding } from "@/features/onboarding/api/onboarding.api";
import { applyScan, scanLabels, SCAN_KEPT_MESSAGE, type ScannedField, useDocScan, type ScanOutcome, StepHeading, FileDrop, ScanStatus, trimmed, ACCOUNT_TYPES } from "@/features/onboarding/components/wizard";
import { PhotoField } from "./PhotoField";
import { type StepProps, type FormState } from "./schema";

export function BankStep({ form, checkFile, onCheckFile, checkError, checkPreviousNote, onScanned, onBusyChange }: StepProps & { checkFile: File | null; onCheckFile: (f: File | null) => void; checkError?: string | null; checkPreviousNote?: string | null; onScanned?: () => void; onBusyChange?: (busy: boolean) => void }) {
  const c = form.control;

  const [revealed, setRevealed] = useState(
    () => Boolean(checkFile || form.getValues("bankRoutingNumber") || form.getValues("bankAccountNumber")));
  const hasBankError = Object.keys(form.formState.errors).some((k) => k.startsWith("bank") || k.startsWith("withdraw"));
  useEffect(() => { if (hasBankError) setRevealed(true); }, [hasBankError]);
  const showFields = revealed;
  const separateWithdraw = form.watch("separateWithdrawAccount");

  const ck = useDocScan({
    reading: "Reading your check…",
    failed: "Something went wrong reading your check. Please enter the routing and account numbers manually.",
    onFile: onCheckFile,
    onBusyChange,
    onPick: () => setRevealed(true),
    run: async (file, onlyEmpty): Promise<ScanOutcome> => {
      const d = await onboarding.scan(file, "voidedCheck");
      const scanned: ScannedField<FormState>[] = [
        ["bankRoutingNumber", trimmed(d.routingNumber), "routing #"],
        ["bankAccountNumber", trimmed(d.accountNumber), "account #"],
        ["bankName", trimmed(d.bankName), "bank name"],
      ];
      const { read, applied } = applyScan(form, scanned, { shouldValidate: form.formState.isSubmitted, onlyEmpty });
      if (read.length === 0) {
        return { kind: "error", msg: "We couldn't read anything off that photo. Try a clearer, well-lit shot, or enter the routing and account numbers manually." };
      }
      onScanned?.();
      return {
        kind: "success",
        msg: applied.length === 0 ? SCAN_KEPT_MESSAGE : `Pre-filled ${scanLabels(applied).join(", ")}. Please confirm against your check.`,
      };
    },
  });

  return (
    <div className="space-y-5">
      <StepHeading
        title="Business bank account"
        subtitle="Where you'll receive deposits and pay processing fees."
      />

      <PhotoField label="Take a photo of a voided business check" required>
        <FileDrop prompt="Take a photo or upload" busy={ck.busy} file={checkFile} error={checkError} previousNote={checkPreviousNote} onFile={(f) => ck.pick(f, !!checkPreviousNote)} onRemove={ck.clear} />
        <ScanStatus msg={ck.msg} kind={ck.kind} onRetry={ck.retry} />
      </PhotoField>

      <Accordion
        type="single"
        collapsible
        value={showFields ? "bank" : ""}
        onValueChange={(v) => setRevealed(v === "bank")}
        className="border-t border-slate-100"
      >
        <AccordionItem value="bank" className="border-b-0">
          <AccordionTrigger className="py-1.5 text-sm font-semibold text-slate-700 hover:no-underline">
            Bank Information
          </AccordionTrigger>
          <AccordionContent className="space-y-4 pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextField control={c} name="bankRoutingNumber" label="Routing number" required inputMode="numeric" maxLength={9} placeholder="123456789" format={(v) => v.replace(/\D/g, "").slice(0, 9)} hint="9 digits." />
              <TextField control={c} name="bankAccountNumber" label="Account number" required inputMode="numeric" maxLength={17} placeholder="Checking account number" format={(v) => v.replace(/\D/g, "").slice(0, 17)} hint="Digits only, up to 17." />
            </div>

            <SwitchField control={c} name="separateWithdrawAccount" label="Use a different account for withdrawals" />

            {separateWithdraw && (
              <div className="space-y-4 pl-4 border-l-2 border-border">
                <TextField control={c} name="withdrawBankName" label="Withdraw bank name" placeholder="e.g. Wells Fargo" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <TextField control={c} name="withdrawRoutingNumber" label="Withdraw routing number" required inputMode="numeric" maxLength={9} placeholder="123456789" format={(v) => v.replace(/\D/g, "").slice(0, 9)} hint="9 digits." />
                  <TextField control={c} name="withdrawAccountNumber" label="Withdraw account number" required inputMode="numeric" maxLength={17} format={(v) => v.replace(/\D/g, "").slice(0, 17)} hint="Digits only, up to 17." />
                </div>
                <RadioField control={c} name="withdrawAccountType" label="Withdraw account type" options={ACCOUNT_TYPES.map((t) => ({ value: t, label: t }))} />
              </div>
            )}

            <p className="text-xs text-slate-400">
              Your information is sent securely and reviewed only by a BLogic
              representative. Nothing is charged or boarded until you've confirmed
              the details with them.
            </p>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
